import axios from "axios";
import { overviewFiltering, processPerformanceParams } from "../../../../../utils/queryUtils";
import { type UpdateMessageRequest, type MessageContent } from "../types";
import { type IMessageDataService } from "../types/services";
import { formatFilters } from "../../../../../utils/filterMapUtils";
import {
  messageFilterTypes,
  type MessageLineChart,
  type MessageEngagement,
  type MessagePerson,
  type MessageSessionApi,
} from "../types/models";
import { type BasePerformanceRequestFilterParams, type PaginationParams } from "features/Library/Common/models";
import { type ChartPeriod } from "hooks/useChartPeriodMeasure";
import { type MessageOverviewDetails } from "features/Reporting/types/content";

const baseMessageApiUrl = "/api/communications/messages";
const baseMessageApiUrlV4 = "/api/v4/communications/messages";

export const MESSAGE_REPORT_LINE_CHART_ENDPOINT = "/api/reports/v1/messages/linechart";
export const MESSAGE_REPORT_ENGAGEMENT_ENDPOINT = "/api/reports/v1/messages/engagement";
export const MESSAGE_REPORT_SESSIONS_ENDPOINT = "/api/reports/v1/messages/sessions";
export const MESSAGE_REPORT_PEOPLE_ENDPOINT = "/api/reports/v1/messages/user-details";
export const MESSAGE_REPORT_OVERVIEW_ENDPOINT = "/api/reports/v1/messages/messages";

const SEND_SESSION_EXPORT_ENDPOINT = "/api/dataexport/v1/messages/send-sessions";
const USER_PROGRESS_EXPORT_ENDPOINT = "/api/dataexport/v1/messages/user-progress";

const messagesDataService: IMessageDataService = {
  updateMessage(data: UpdateMessageRequest) {
    const { id, ...payload } = data;
    return axios.put(`${baseMessageApiUrl}/${id}`, payload);
  },
  updateMessageContent(data: MessageContent) {
    const { id, ...payload } = data;
    return axios.put(`${baseMessageApiUrl}/${id}/content`, payload);
  },
  getMessageWithCancel({ id }) {
    const source = axios.CancelToken.source();
    return {
      getDataPromise: axios.get(`${baseMessageApiUrl}/${id}`, {
        cancelToken: source?.token,
      }),
      cancel: source.cancel,
    };
  },
  createMessageSend(messageId, opts) {
    return axios.post(`${baseMessageApiUrlV4}/${messageId}/send-sessions`, opts);
  },
  deleteMessages(ids: number[]) {
    const data = {
      messageIds: ids,
    };
    return axios.delete(baseMessageApiUrl, { data });
  },

  getPublishedMessages({ filters, ...rest }, abortController: AbortController) {
    return axios.get(`${baseMessageApiUrl}/published`, {
      params: { ...rest, ...formatFilters(filters!, messageFilterTypes) },
      paramsSerializer: overviewFiltering,
      signal: abortController.signal,
    });
  },

  getPublishedMessagesByIds(ids, showPurchased) {
    const config = {
      params: {
        showPurchased,
        filter: ids.map((x) => `(PrimaryId eq ${x})`).join(" or "),
      },
    };

    return axios.get(`${baseMessageApiUrl}/published`, config);
  },

  getMessagesV4Async({ filterQueryParams, ...rest }) {
    const source = axios.CancelToken.source();
    return {
      getDataPromise: axios.get("/api/content", {
        params: { ...rest, ...filterQueryParams },
        cancelToken: source?.token,
        paramsSerializer: overviewFiltering,
      }),
      cancel: source.cancel,
    };
  },

  // Performance reporting endpoints
  getMessageLineChart(
    filter: BasePerformanceRequestFilterParams,
    messageId: number | undefined,
    aggregation: ChartPeriod,
    signal?: AbortSignal,
    sendSessionId?: number,
  ) {
    return axios.get<MessageLineChart[]>(MESSAGE_REPORT_LINE_CHART_ENDPOINT, {
      params: { ...filter, messageId, aggregation, sendSessionId },
      signal,
      paramsSerializer: processPerformanceParams,
    });
  },

  getMessageEngagement(
    filter: BasePerformanceRequestFilterParams,
    messageId: number | undefined,
    signal?: AbortSignal,
    sendSessionId?: number,
  ) {
    return axios.get<MessageEngagement>(MESSAGE_REPORT_ENGAGEMENT_ENDPOINT, {
      params: { ...filter, messageId, sendSessionId },
      signal,
      paramsSerializer: processPerformanceParams,
    });
  },

  getMessageSessions(
    filter: BasePerformanceRequestFilterParams,
    messageId: number,
    signal?: AbortSignal,
    pagination?: PaginationParams,
  ) {
    return axios.get<MessageSessionApi[]>(MESSAGE_REPORT_SESSIONS_ENDPOINT, {
      params: { ...filter, messageId, ...pagination },
      signal,
      paramsSerializer: processPerformanceParams,
    });
  },

  getMessagePeople(
    filter: BasePerformanceRequestFilterParams,
    messageId: number,
    signal?: AbortSignal,
    sendSessionId?: number,
    pagination?: PaginationParams,
  ) {
    return axios.get<MessagePerson[]>(MESSAGE_REPORT_PEOPLE_ENDPOINT, {
      params: { ...filter, messageId, sendSessionId, ...pagination },
      signal,
      paramsSerializer: processPerformanceParams,
    });
  },

  getMessagesOverview(filter: BasePerformanceRequestFilterParams, signal?: AbortSignal, pagination?: PaginationParams) {
    return axios.get<MessageOverviewDetails[]>(MESSAGE_REPORT_OVERVIEW_ENDPOINT, {
      params: { ...filter, ...pagination },
      signal,
      paramsSerializer: processPerformanceParams,
    });
  },

  getMessageExport(params: BasePerformanceRequestFilterParams, abortController: AbortController) {
    return axios.get(SEND_SESSION_EXPORT_ENDPOINT, {
      params,
      responseType: "blob",
      paramsSerializer: processPerformanceParams,
      signal: abortController.signal,
    });
  },

  getMessageDrillDownExport(
    params: BasePerformanceRequestFilterParams,
    sendSessionId: number,
    abortController: AbortController,
  ) {
    return axios.get(USER_PROGRESS_EXPORT_ENDPOINT, {
      params: { ...params, sendSessionId },
      responseType: "blob",
      paramsSerializer: processPerformanceParams,
      signal: abortController.signal,
    });
  },
};

export default messagesDataService;
